
import numbro from 'numbro'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import CreateCustomModel from '@/components/CreateCustomModel.vue'
import DeletedModels from '@/components/DeletedModels.vue'
import EditModelData from '@/components/EditModelData.vue'
import PlanImporter from '@/components/PlanImporter.vue'
import ModelSettings from '@/components/ModelSettings.vue'
import { hasModelReport, getModelReportQuality } from '@/utils/modelReport.js'
import { copyTextToClipboard } from '@/utils/clipboard.js'
import _ from 'lodash'
import {
  countFamilyRows, familyRowsPage, filterFamilyRows, groupByFamily, lastFamilyPage, latestInFamily, sortFamilyRows,
  versionFamilyId, versionNumber
} from '@/utils/modelVersions.js'

export default {
  data () {
    return {
      currentTab: 0,
      createDialog: false,
      modelDataDialog: false,
      deleteDialog: false,
      deleteItem: '',
      editItem: null,
      cloning: false,
      maxNameLength: v => v.length <= 20 || this.$t('views.manageCustomModels.inputTooLong'),
      snack: false,
      snackColor: '',
      snackText: '',
      search: '',
      debouncedSearch: '',
      importItem: null,
      importDialog: false,
      settingsItem: null,
      settingsDialog: false,
      settingsInitialTab: 0,
      selectedCustomModels: [],
      deleteSelectedDialog: false,
      enableDeleteSelectedCustomModels: null,
      processedCustomModels: [],
      customModelsLoading: true,
      processedMmmModels: [],
      // Ad-Hoc version families expanded in the table, and the table's sort and page
      expandedAdHocFamilies: [],
      adHocSortBy: [],
      adHocSortDesc: [],
      adHocPage: 1,
      adHocItemsPerPage: 10,
      mmmModelsLoading: false,
      mmmModelsLoaded: false,
      processedAutomaticModels: [],
      automaticModelsLoading: true,
      processedMetadataModels: [],
      metadataModelsActive: false,
      metadataTypeMenu: false,
      metadataExcludedTypes: [],
      // Sentinel for the "(none)" funnel option, representing a blank metadata value.
      metadataBlankSentinel: '__NG_BLANK__',
      // Per-field funnel filter: prop -> array of values to HIDE (empty = show all).
      metadataExcludedValues: {
        _metaClient: [],
        _metaBrand: [],
        _metaCategory: [],
        _metaSubCategory: [],
        _metaCountry: []
      },
      // Per-field v-menu open state (pre-keyed so Vue 2.7 tracks it without $set).
      metadataValueMenus: {
        _metaClient: false,
        _metaBrand: false,
        _metaCategory: false,
        _metaSubCategory: false,
        _metaCountry: false
      },
      // --- Channel Taxonomy tab ---
      // One lean row per model x media channel, built lazily on first open.
      processedTaxonomyRows: [],
      taxonomyRowsActive: false,
      taxonomyTypeMenu: false,
      taxonomyExcludedTypes: [],
      // Same exclusion semantics as the Metadata tab: prop -> values to HIDE.
      // Pre-keyed so Vue 2.7 tracks them without $set.
      taxonomyExcludedValues: {
        _channelName: [], _l0: [], _l1: [], _l2: [], _l3: [], _l4: [], _l5: [], _l6: []
      },
      taxonomyValueMenus: {
        _channelName: false, _l0: false, _l1: false, _l2: false, _l3: false, _l4: false, _l5: false, _l6: false
      },
      // --- Ad-Hoc tab filters (ported from the App.vue Model Selection modal) ---
      // Prefixed adHoc* to avoid colliding with the Metadata-tab filter state above.
      selectedAdHocCountries: [],
      adHocCountrySearch: '',
      adHocCountryMenu: false,
      selectedAdHocBrands: [],
      adHocBrandSearch: '',
      adHocBrandMenu: false,
      // One config per curated client_metadata category surfaced as a column + filter.
      // `key` is the client_metadata key; `valueField` is the lean processedMmmModels prop.
      adHocMetadataFilterConfigs: [
        { key: 'client', valueField: '_metaClient', icon: 'mdi-domain', headerKey: 'metaHeaderClient', filterKey: 'filterByClient', searchKey: 'searchClients', allKey: 'allClients', selectedKey: 'clientsSelected' },
        { key: 'category', valueField: '_metaCategory', icon: 'mdi-shape', headerKey: 'metaHeaderCategory', filterKey: 'filterByCategory', searchKey: 'searchCategories', allKey: 'allCategories', selectedKey: 'categoriesSelected' },
        { key: 'sub-category', valueField: '_metaSubCategory', icon: 'mdi-shape-outline', headerKey: 'metaHeaderSubCategory', filterKey: 'filterBySubcategory', searchKey: 'searchSubcategories', allKey: 'allSubcategories', selectedKey: 'subcategoriesSelected' }
      ],
      // Pre-keyed so Vue 2.7 tracks them without $set.
      adHocMetadataSelected: { client: [], category: [], 'sub-category': [] },
      adHocMetadataSearch: { client: '', category: '', 'sub-category': '' },
      adHocMetadataMenu: { client: false, category: false, 'sub-category': false }
    }
  },
  methods: {
    ...mapActions('admin', [
      'recalculateModelReport'
    ]),
    ...mapActions('manager', [
      'loadCustomModels',
      'loadMmmModels',
      'loadReferenceModels',
      'loadEkrmModels',
      'deleteCustomModel',
      'renameCustomModel',
      'deleteCustomModelsBulk'
    ]),
    ...mapActions('clients', [
      'loadClients',
      'loadGroupClients'
    ]),
    hasModelReport,
    qualityFor (item) {
      return getModelReportQuality(item)
    },
    creatorLabel (item) {
      // Resolve the displayed creator value (name when present, else the legacy
      // created_by fallback), null-safe for items without created_by.
      const raw = item.created_by_name ||
        (item.created_by ? item.created_by.slice(item.created_by.indexOf('_') + 1) : '')
      if (!raw) return raw
      // Only transform when the value is an email whose local part is exactly two
      // words separated by a single dot: w.x@y.z  ->  "W X".
      const match = String(raw).match(/^([^.@\s]+)\.([^.@\s]+)@[^@\s]+$/)
      if (!match) return raw
      const cap = w => w.charAt(0).toUpperCase() + w.slice(1)
      return cap(match[1]) + ' ' + cap(match[2])
    },
    getHighlightedText (text, searchQuery) {
      if (!searchQuery || !text) {
        return null
      }
      // Convert to string if it's not already
      text = String(text)
      // Escape special regex characters in the search query
      const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // Create case-insensitive regex
      const regex = new RegExp(`(${escapedQuery})`, 'gi')
      // Replace matches with highlighted version
      return text.replace(regex, '<span class="search-highlight">$1</span>')
    },
    updateProcessedCustomModels () {
      // Clear old array to help garbage collection
      this.processedCustomModels = []

      let models = []
      if (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isScientist) {
        models = this.customModels.filter(model => model.type !== 'automatic')
      } else {
        const directAccessModels = this.clients.map(client => client.id)
        const groupAccessModels = this.groupClients.map(client => client.id)
        models = this.customModels.filter(model => model.type !== 'automatic' && (this.authUser.uid === model.created_by || directAccessModels.includes(model.id) || groupAccessModels.includes(model.id)))
      }

      // MEMORY OPTIMIZATION: No longer pre-computing highlighted text for all models
      // Highlighting is now computed on-demand in the template for visible rows only
      this.processedCustomModels = models.map(model => {
        // Don't spread the entire model - just add computed properties
        // The model object itself will be accessed directly in templates
        const resultModel = {
          ...model,
          // Cache country name to avoid repeated lookups
          _countryName: model.custom_model && model.custom_model.country
            ? this.getCountryName(model.custom_model.country)
            : this.$t('views.manageCustomModels.notAvailable')
        }

        // MEMORY OPTIMIZATION: Keep only the quality field for the table.
        // Prefer the small Firestore-mirrored summary; fall back to parsing the legacy inline string.
        if (model.model_report_summary && model.model_report_summary.quality !== undefined) {
          resultModel.model_report = { quality: model.model_report_summary.quality }
        } else if (model.model_report && typeof model.model_report === 'string') {
          try {
            const parsed = JSON.parse(model.model_report)
            resultModel.model_report = { quality: parsed.quality }
          } catch (e) {
            resultModel.model_report = { quality: null }
          }
        } else if (model.model_report && typeof model.model_report === 'object') {
          resultModel.model_report = { quality: model.model_report.quality }
        }

        return resultModel
      })
    },
    updateProcessedMmmModels () {
      // Clear old array to help garbage collection
      this.processedMmmModels = []

      // This view lists every Ad-Hoc model, so the latest version of each family is its current one
      const families = groupByFamily(this.mmmModels)

      // MEMORY OPTIMIZATION: Pre-compute country names and minimize reactive data
      this.processedMmmModels = this.mmmModels.map(model => {
        // Pre-compute country name to avoid repeated function calls during rendering
        const countryName = this.getAdHocCountryName(model)

        // Check if model has last_data for the database edit button
        const hasLastData = model.reference_model &&
                           model.reference_model.config &&
                           model.reference_model.config.dates &&
                           'last_data' in model.reference_model.config.dates

        const versions = families.get(versionFamilyId(model))

        return {
          ...model,
          // Cache computed values
          _countryName: countryName,
          _hasLastData: hasLastData,
          // Flat fields for the modal-style columns / search / sort
          brandNames: this.getAdHocBrands(model),
          _metaClient: this.getMetadataValue(model, 'client') || '',
          _metaCategory: this.getMetadataValue(model, 'category') || '',
          _metaSubCategory: this.getMetadataValue(model, 'sub-category') || '',
          versionCount: versions.length,
          isOlderVersion: latestInFamily(versions).id !== model.id
        }
      })
    },
    versionNumber,
    // date_created: in production importModel creates the doc on deploy, so it is the deploy date.
    versionDate (model) {
      return model.date_created ? moment(model.date_created.toDate()).format('YYYY/MM/DD HH:mm') : ''
    },
    toggleAdHocFamily (item) {
      const familyId = versionFamilyId(item)
      this.expandedAdHocFamilies = this.expandedAdHocFamilies.includes(familyId)
        ? this.expandedAdHocFamilies.filter(id => id !== familyId)
        : this.expandedAdHocFamilies.concat(familyId)
    },
    isAdHocFamilyExpanded (item) {
      return this.expandedAdHocFamilies.includes(versionFamilyId(item))
    },
    adHocItemClass (item) {
      return item.isOlderVersion ? 'ad-hoc-version-row' : ''
    },
    cancelSelectedCustomModels () {
      this.deleteSelectedDialog = false
      this.enableDeleteSelectedCustomModels = null
    },
    async deleteSelectedCustomModels () {
      this.deleteSelectedDialog = false
      this.enableDeleteSelectedCustomModels = null
      try {
        const result = await this.deleteCustomModelsBulk({ selectedModels: this.selectedCustomModels })
        if (result.failed.length > 0) {
          this.snackColor = 'error'
          this.snackText = this.$t('views.manageCustomModels.errorDeletingSomeModels', { count: result.failed.length })
          this.snack = true
        }
      } catch (error) {
        console.error(error)
        this.snackColor = 'error'
        this.snackText = this.$t('views.manageCustomModels.errorDeletingModel')
        this.snack = true
      }
      this.selectedCustomModels = []
    },
    stillExists (item) {
      if (this.allReferences.includes(item.reference_model.codename)) {
        return true
      } else {
        return false
      }
    },
    triggerRecalculateModelReport (item) {
      // if ('model_report' in item) {
      this.recalculateModelReport(item.id).then(result => {
        console.log('Calculating new model report')
        console.log(result)
      }).catch(error => {
        console.log(error)
      })
      // }
    },
    triggerDeleteCustomModel (clientId) {
      this.deleteCustomModel({ client: clientId }).catch(error => {
        console.error(error)
        this.snackColor = 'error'
        this.snackText = this.$t('views.manageCustomModels.errorDeletingModel')
        this.snack = true
      })
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    dateModified (item) {
      const dateModified = item.date_edited ? item.date_edited : item.date_created
      if (dateModified) {
        return dateModified.toDate().toString().slice(4, 24)
      } else {
        return null
      }
    },
    dateModifiedDate (item) {
      // Chip label: the date part only (matches the Model Selection modal).
      const date = item.date_edited ? item.date_edited : item.date_created
      return date ? moment(date.toDate()).format('YYYY/MM/DD') : null
    },
    dateModifiedTime (item) {
      // Tooltip content: the time part (matches the Model Selection modal).
      const date = item.date_edited ? item.date_edited : item.date_created
      return date ? moment(date.toDate()).format('HH:mm') : ''
    },
    metadataDateShort (item) {
      // Compact chip label, e.g. "Jul 23 2025". Falls back to date_created by
      // truthiness, matching dateModified(); the day is zero-padded by toString().
      const ts = item.date_edited ? item.date_edited : item.date_created
      if (!ts || typeof ts.toDate !== 'function') {
        return null
      }
      // "Wed Jul 23 2025 14:30:00 GMT..." -> "Jul 23 2025"
      return ts.toDate().toString().slice(4, 15).trim()
    },
    metadataDateFull (item) {
      // Tooltip content: full localized date and time.
      const ts = item.date_edited ? item.date_edited : item.date_created
      if (!ts || typeof ts.toDate !== 'function') {
        return null
      }
      return ts.toDate().toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    customSort: function (items, sortBy, sortDesc, locale, customSorters) {
      const sortCol = sortBy[0]
      if (sortCol === 'date_created') {
        if (sortDesc[0]) {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return aDate - bDate
          })
        } else {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return bDate - aDate
          })
        }
      } else {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return _.get(b, sortCol, '').toString().localeCompare(_.get(a, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return _.get(a, sortCol, '').toString().localeCompare(_.get(b, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    },
    customSortMetadata (items, sortBy, sortDesc) {
      // Dedicated sort for the Metadata tab. Uses a strict (transitive) total
      // order so rows never come out partially sorted — unlike the locale
      // numeric collation in customSort, which is non-transitive for some
      // mixed alphanumeric names and leaves rows out of order.
      const sortCol = sortBy[0]
      const desc = sortDesc[0]
      if (!sortCol) {
        return items
      }
      if (sortCol === 'date_created') {
        const toMs = item => {
          // Match dateModified(): the displayed value falls back to date_created
          // by truthiness, not by key presence — the lean row always has a
          // date_edited key (often undefined), so 'date_edited' in item is useless here.
          const ts = item.date_edited ? item.date_edited : item.date_created
          if (!ts) return 0
          if (typeof ts.toMillis === 'function') return ts.toMillis()
          if (typeof ts.getTime === 'function') return ts.getTime()
          return Number(ts) || 0
        }
        // Match the other tabs: first click (ascending) shows newest first.
        items.sort((a, b) => desc ? toMs(a) - toMs(b) : toMs(b) - toMs(a))
      } else {
        // Locale-aware, case/accent-insensitive collation so accented names
        // (á, ñ, …) sort in their expected place. No `numeric` option: that
        // collation is non-transitive and leaves rows out of order.
        // ignorePunctuation + trim so leading spaces/punctuation don't push a
        // name ahead of the digits (those characters collate below 0-9 / A-Z).
        const collator = new Intl.Collator(undefined, { sensitivity: 'base', ignorePunctuation: true })
        const asText = item => {
          const raw = _.get(item, sortCol)
          return raw === null || raw === undefined ? '' : String(raw).trim()
        }
        items.sort((a, b) => {
          const cmp = collator.compare(asText(a), asText(b))
          return desc ? -cmp : cmp
        })
      }
      return items
    },
    saveName (item) {
      if (item && item.name) {
        const name = item.name.trim()
        item.name = this.$t('views.manageCustomModels.updating')
        this.renameCustomModel({
          client: item.id,
          name: name
        }).then(() => {
          this.snack = true
          this.snackColor = 'success'
          this.snackText = this.$t('views.manageCustomModels.modelRenamed')
        }).catch(error => {
          // Reload models, to get rid of (non)updated names
          this.loadCustomModels()
          console.log(error)
          this.snack = true
          this.snackColor = 'error'
          this.snackText = this.$t('views.manageCustomModels.errorRenamingModel')
        })
      }
    },
    viewJSON: function (item) {
      console.log(JSON.stringify(item))
    },
    getCountryName: function (countryCode) {
      const countryObj = this.countries.find(c => c.id === countryCode)
      return countryObj ? countryObj.name : countryCode || this.$t('views.manageCustomModels.notAvailable')
    },
    getAdHocCountryName: function (item) {
      const countryCode = this.getAdHocCountryCode(item)
      return countryCode ? this.getCountryName(countryCode) : this.$t('views.manageCustomModels.notAvailable')
    },
    getAdHocCountryCode (item) {
      // Prefer the curated client_metadata.country when set, else the first brand's
      // country in reference_model.config.brands (all brands share the same country).
      if (item.client_metadata && item.client_metadata.country) {
        return item.client_metadata.country
      }
      if (item.reference_model && item.reference_model.config && item.reference_model.config.brands) {
        const firstBrandKey = Object.keys(item.reference_model.config.brands)[0]
        if (firstBrandKey && item.reference_model.config.brands[firstBrandKey]) {
          return item.reference_model.config.brands[firstBrandKey].country
        }
      }
      return null
    },
    resolveCountryKey (value) {
      // Collapses the same country expressed two ways (a registry code like "MX"
      // from old model data vs. a name like "Mexico" from client_metadata) into a
      // single canonical key so the country filter doesn't list it twice.
      if (value === null || value === undefined) {
        return null
      }
      const str = value.toString().trim()
      if (str === '') {
        return null
      }
      if (this.countries.some(c => c.id === str)) {
        return str
      }
      const byName = this.countries.find(c => c.name && c.name.toLowerCase() === str.toLowerCase())
      if (byName) {
        return byName.id
      }
      return str.toLowerCase()
    },
    getMetadataValue (item, key) {
      // Curated client_metadata value for the given key (no fallback, unlike country/brand).
      return (item.client_metadata && item.client_metadata[key]) ? item.client_metadata[key] : null
    },
    getModelBrands (item) {
      // Prefer the curated client_metadata.brand, else the reference_model.config.brands keys.
      if (item.client_metadata && item.client_metadata.brand) {
        return [item.client_metadata.brand]
      }
      if (item.reference_model && item.reference_model.config && item.reference_model.config.brands) {
        return Object.keys(item.reference_model.config.brands)
      }
      return []
    },
    getAdHocBrands (item) {
      const brands = this.getModelBrands(item)
      return brands.length > 0 ? brands.join(', ') : this.$t('views.manageCustomModels.notAvailable')
    },
    isNaValue (val) {
      // Empty fields ('') and the localized N/A sentinel are both "not available".
      return !val || val === this.$t('views.manageCustomModels.notAvailable')
    },
    // --- Ad-Hoc Country filter ---
    toggleAdHocCountry (countryValue) {
      if (this.selectedAdHocCountries.includes(countryValue)) {
        this.selectedAdHocCountries = this.selectedAdHocCountries.filter(c => c !== countryValue)
      } else {
        this.selectedAdHocCountries = [...this.selectedAdHocCountries, countryValue]
      }
    },
    toggleSelectAllAdHocCountries () {
      const search = this.adHocCountrySearch
      const visibleValues = this.availableAdHocCountries
        .filter(c => !search || c.text.toLowerCase().includes(search.toLowerCase()))
        .map(c => c.value)
      const allVisibleSelected = visibleValues.every(v => this.selectedAdHocCountries.includes(v))
      if (allVisibleSelected) {
        this.selectedAdHocCountries = this.selectedAdHocCountries.filter(v => !visibleValues.includes(v))
      } else {
        this.selectedAdHocCountries = Array.from(new Set([...this.selectedAdHocCountries, ...visibleValues]))
      }
    },
    // --- Ad-Hoc Brand filter ---
    toggleAdHocBrand (brandValue) {
      if (this.selectedAdHocBrands.includes(brandValue)) {
        this.selectedAdHocBrands = this.selectedAdHocBrands.filter(b => b !== brandValue)
      } else {
        this.selectedAdHocBrands = [...this.selectedAdHocBrands, brandValue]
      }
    },
    toggleSelectAllAdHocBrands () {
      const search = this.adHocBrandSearch
      const visibleValues = this.availableAdHocBrands
        .filter(b => !search || b.text.toLowerCase().includes(search.toLowerCase()))
        .map(b => b.value)
      const allVisibleSelected = visibleValues.every(v => this.selectedAdHocBrands.includes(v))
      if (allVisibleSelected) {
        this.selectedAdHocBrands = this.selectedAdHocBrands.filter(v => !visibleValues.includes(v))
      } else {
        this.selectedAdHocBrands = Array.from(new Set([...this.selectedAdHocBrands, ...visibleValues]))
      }
    },
    // --- Ad-Hoc Metadata filters (client / category / sub-category) ---
    availableAdHocMetadata (key) {
      // Unique non-blank values for the metadata key across the ad-hoc pool, so the
      // filter only offers values that actually appear among the models listed.
      const values = new Set()
      this.mmmModels.forEach(item => {
        const val = this.getMetadataValue(item, key)
        if (val !== null) {
          values.add(val)
        }
      })
      return Array.from(values).sort((a, b) => a.localeCompare(b)).map(val => ({ value: val, text: val }))
    },
    filteredAdHocMetadata (key) {
      const search = this.adHocMetadataSearch[key]
      if (!search) {
        return this.availableAdHocMetadata(key)
      }
      return this.availableAdHocMetadata(key).filter(opt =>
        opt.text.toLowerCase().includes(search.toLowerCase())
      )
    },
    toggleAdHocMetadata (key, value) {
      if (this.adHocMetadataSelected[key].includes(value)) {
        this.adHocMetadataSelected[key] = this.adHocMetadataSelected[key].filter(v => v !== value)
      } else {
        this.adHocMetadataSelected[key] = [...this.adHocMetadataSelected[key], value]
      }
    },
    toggleSelectAllAdHocMetadata (key) {
      const search = this.adHocMetadataSearch[key]
      const visibleValues = this.availableAdHocMetadata(key)
        .filter(opt => !search || opt.text.toLowerCase().includes(search.toLowerCase()))
        .map(opt => opt.value)
      const allVisibleSelected = visibleValues.every(v => this.adHocMetadataSelected[key].includes(v))
      if (allVisibleSelected) {
        this.adHocMetadataSelected[key] = this.adHocMetadataSelected[key].filter(v => !visibleValues.includes(v))
      } else {
        this.adHocMetadataSelected[key] = Array.from(new Set([...this.adHocMetadataSelected[key], ...visibleValues]))
      }
    },
    hasActiveAdHocMetadataFilter (key) {
      // True when some (but not all) values are selected.
      const total = this.availableAdHocMetadata(key).length
      const selectedCount = this.adHocMetadataSelected[key].length
      return selectedCount > 0 && selectedCount < total
    },
    selectAllAdHocMetadataIcon (key) {
      const search = this.adHocMetadataSearch[key]
      const visibleValues = this.availableAdHocMetadata(key)
        .filter(opt => !search || opt.text.toLowerCase().includes(search.toLowerCase()))
        .map(opt => opt.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.adHocMetadataSelected[key].includes(v))
      const someVisibleSelected = visibleValues.some(v => this.adHocMetadataSelected[key].includes(v))
      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllAdHocMetadataText (key) {
      const search = this.adHocMetadataSearch[key]
      const visible = this.availableAdHocMetadata(key)
        .filter(opt => !search || opt.text.toLowerCase().includes(search.toLowerCase()))
      if (search && visible.length > 0) {
        return this.$t('views.manageCustomModels.selectAllVisible', { count: visible.length })
      }
      return this.$t('views.manageCustomModels.filterSelectAll')
    },
    initAdHocFilterSelections () {
      // No open/close event on a persistent tab: default brands/countries to "all selected"
      // so the strict-subset filter semantics mean "show everything" until the user narrows.
      this.selectedAdHocCountries = this.availableAdHocCountries.map(c => c.value)
      this.selectedAdHocBrands = this.availableAdHocBrands.map(b => b.value)
    },
    customFilter (value, search, item) {
      if (!search) return true

      const searchLower = search.toLowerCase()

      // Search in all visible fields
      const searchableFields = [
        item.name,
        item.id,
        _.get(item, 'reference_model.config.category', ''),
        _.get(item, 'custom_model.brand', ''),
        item._countryName,
        this.formatValue(_.get(item, 'custom_model.average_monthly_invest', '')),
        this.formatValue(_.get(item, 'custom_model.average_monthly_sales', '')),
        _.get(item, 'model_report.quality', ''),
        this.dateModified(item),
        this.creatorLabel(item)
      ]

      // Check if any field contains the search term
      return searchableFields.some(field => {
        if (field === null || field === undefined) return false
        return String(field).toLowerCase().includes(searchLower)
      })
    },
    updateProcessedAutomaticModels () {
      this.processedAutomaticModels = []

      let models = []
      if (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isScientist) {
        models = this.customModels.filter(model => model.type === 'automatic')
      } else {
        const directAccessModels = this.clients.map(client => client.id)
        const groupAccessModels = this.groupClients.map(client => client.id)
        models = this.customModels.filter(model => model.type === 'automatic' && (this.authUser.uid === model.created_by || directAccessModels.includes(model.id) || groupAccessModels.includes(model.id)))
      }

      this.processedAutomaticModels = models.map(model => {
        const resultModel = {
          ...model,
          _countryName: model.custom_model && model.custom_model.country
            ? this.getCountryName(model.custom_model.country)
            : this.$t('views.manageCustomModels.notAvailable')
        }

        if (model.model_report_summary && model.model_report_summary.quality !== undefined) {
          resultModel.model_report = { quality: model.model_report_summary.quality }
        } else if (model.model_report && typeof model.model_report === 'string') {
          try {
            const parsed = JSON.parse(model.model_report)
            resultModel.model_report = { quality: parsed.quality }
          } catch (e) {
            resultModel.model_report = { quality: null }
          }
        } else if (model.model_report && typeof model.model_report === 'object') {
          resultModel.model_report = { quality: model.model_report.quality }
        }

        return resultModel
      })
    },
    customFilterAutomatic (value, search, item) {
      if (!search) return true

      var searchLower = search.toLowerCase()

      var searchableFields = [
        item.name,
        item.id,
        item._countryName,
        this.creatorLabel(item)
      ]

      return searchableFields.some(function (field) {
        if (field === null || field === undefined) return false
        return String(field).toLowerCase().includes(searchLower)
      })
    },
    matchesAdHocSearch (item, search) {
      const searchLower = search.toLowerCase()

      // Search across the visible Ad-Hoc columns
      const searchableFields = [
        item.name,
        item.id,
        item._countryName,
        item.brandNames,
        item._metaClient,
        item._metaCategory,
        item._metaSubCategory
      ]

      // Check if any field contains the search term
      return searchableFields.some(field => {
        if (field === null || field === undefined) return false
        return String(field).toLowerCase().includes(searchLower)
      })
    },
    copyIdToClipboard (id) {
      copyTextToClipboard(id)
        .then(copied => {
          if (!copied) {
            console.error('Failed to copy id')
            return
          }
          this.snack = true
          this.snackColor = 'success'
          this.snackText = this.$t('views.manageCustomModels.idCopied')
        })
    },
    modelTypeKey (model) {
      // Stable, locale-independent classifier for the Type column / filter.
      if (model.type === 'mmm') return 'mmm'
      if (model.type === 'automatic') return 'automatic'
      if (model.type === 'ekrm') return 'ekrm'
      return 'reference'
    },
    typeLabelForKey (key) {
      if (key === 'mmm') return this.$t('views.manageCustomModels.typeAdHoc')
      if (key === 'automatic') return this.$t('views.manageCustomModels.typeDiy')
      if (key === 'ekrm') return this.$t('views.manageCustomModels.typeCustomEkrm')
      return this.$t('views.manageCustomModels.typeCustomReference')
    },
    modelTypeLabel (model) {
      return this.typeLabelForKey(this.modelTypeKey(model))
    },
    typeOptionsFrom (rows) {
      // Spreadsheet-style Type filter choices: fixed display order, limited to
      // the type keys actually present in the given rows. Labels are localized.
      const present = new Set(rows.map(row => row._typeKey))
      return ['reference', 'ekrm', 'mmm', 'automatic']
        .filter(key => present.has(key))
        .map(key => ({ key: key, label: this.typeLabelForKey(key) }))
    },
    isMetadataTypeSelected (key) {
      return !this.metadataExcludedTypes.includes(key)
    },
    toggleMetadataType (key) {
      const idx = this.metadataExcludedTypes.indexOf(key)
      if (idx === -1) {
        this.metadataExcludedTypes.push(key)
      } else {
        this.metadataExcludedTypes.splice(idx, 1)
      }
    },
    toggleMetadataAllTypes () {
      // If anything is hidden, show everything; otherwise hide every present type.
      if (this.metadataExcludedTypes.length > 0) {
        this.metadataExcludedTypes = []
      } else {
        this.metadataExcludedTypes = this.metadataTypeOptions.map(opt => opt.key)
      }
    },
    // --- Per-column funnel helpers, shared by the Metadata and Channel Taxonomy
    // tabs. `excluded` is the tab's pre-keyed { prop -> value[] } state bag and
    // `columns` its column definitions; the logic is identical for both.
    isValueSelected (excluded, prop, value) {
      return !excluded[prop].includes(value)
    },
    toggleValue (excluded, prop, value) {
      const arr = excluded[prop]
      const idx = arr.indexOf(value)
      if (idx === -1) {
        arr.push(value)
      } else {
        arr.splice(idx, 1)
      }
    },
    toggleAllValues (excluded, columns, prop) {
      // If anything is hidden for this field, show everything; otherwise hide all present values.
      if (excluded[prop].length > 0) {
        excluded[prop] = []
      } else {
        const col = columns.find(c => c.prop === prop)
        excluded[prop] = col ? col.options.map(opt => opt.value) : []
      }
    },
    allValuesSelected (excluded, prop) {
      return excluded[prop].length === 0
    },
    valueFilterActive (excluded, prop) {
      return excluded[prop].length > 0
    },
    openMetadataSettings (item) {
      // The Metadata rows are lean (no custom_model/reference_model/client_metadata),
      // but ModelSettings needs the full model — resolve it by id from the getters on demand.
      const full = this.customModels.find(m => m.id === item.id) || this.mmmModels.find(m => m.id === item.id)
      if (full) {
        this.settingsItem = full
        // Open ModelSettings directly on its Metadata tab (index 2).
        this.settingsInitialTab = 2
        this.settingsDialog = true
      }
    },
    updateProcessedMetadataModels () {
      // Clear old array to help garbage collection
      this.processedMetadataModels = []

      // Lazy: only build once the Metadata tab has been opened
      if (!this.metadataModelsActive) {
        return
      }

      // MEMORY OPTIMIZATION: build a LEAN object per model. Do NOT spread the full
      // model — that would hold model_report / custom_model / reference_model.
      const allModels = this.customModels.concat(this.mmmModels)
      this.processedMetadataModels = allModels.map(model => {
        // Curated client_metadata values (flat strings; '' when absent). The keys
        // mirror ModelSettings' metadataFields; note the dashed 'sub-category' key.
        const cm = model.client_metadata || {}
        return {
          id: model.id,
          name: model.name,
          _typeKey: this.modelTypeKey(model),
          _typeLabel: this.modelTypeLabel(model),
          date_created: model.date_created,
          date_edited: model.date_edited,
          created_by: model.created_by,
          created_by_name: model.created_by_name,
          _metaClient: cm.client || '',
          _metaBrand: cm.brand || '',
          _metaCategory: cm.category || '',
          _metaSubCategory: cm['sub-category'] || '',
          _metaCountry: cm.country || ''
        }
      })
    },
    openTaxonomySettings (item) {
      // The taxonomy rows are lean and channel-scoped; ModelSettings needs the
      // full model, resolved by id from the getters on demand.
      const full = this.customModels.find(m => m.id === item._modelId) || this.mmmModels.find(m => m.id === item._modelId)
      if (full) {
        this.settingsItem = full
        // Open ModelSettings directly on its Channel Taxonomy tab (index 3).
        this.settingsInitialTab = 3
        this.settingsDialog = true
      }
    },
    copyTaxonomyCode (code) {
      copyTextToClipboard(code)
        .then(copied => {
          if (!copied) {
            console.error('Failed to copy taxonomy code')
            return
          }
          this.snack = true
          this.snackColor = 'success'
          this.snackText = this.$t('views.manageCustomModels.taxonomyCodeCopied')
        })
    },
    updateProcessedTaxonomyRows () {
      // Clear old array to help garbage collection
      this.processedTaxonomyRows = []

      // Lazy: only build once the Channel Taxonomy tab has been opened
      if (!this.taxonomyRowsActive) {
        return
      }

      // One row per model x media channel. Rows are LEAN (no spread of the model)
      // for the same reason as the Metadata tab, and there are many more of them.
      const allModels = this.customModels.concat(this.mmmModels)
      const rows = []
      allModels.forEach(model => {
        const media = (model.reference_model && model.reference_model.config && model.reference_model.config.media)
          ? model.reference_model.config.media
          : null
        if (!media) {
          return
        }
        const prettyNames = (model.pretty_names && model.pretty_names.media) ? model.pretty_names.media : {}
        const savedTaxonomy = model.channel_taxonomy || {}
        const typeKey = this.modelTypeKey(model)
        const typeLabel = this.typeLabelForKey(typeKey)
        // "Modified" is a model-level attribute, so it is resolved once per model
        // instead of once per channel row: the raw timestamps are shared references
        // (they feed customSortMetadata's date branch and the chip/tooltip), and the
        // formatted short date is built a single time for the search blob.
        const modifiedText = this.metadataDateShort(model) || ''
        Object.keys(media).forEach(channelKey => {
          // Saved L0-L5 values are { name, abbreviation } objects; L6 is free text.
          const saved = savedTaxonomy[channelKey] || {}
          const row = {
            id: model.id + '::' + channelKey,
            _modelId: model.id,
            _modelName: model.name,
            _typeKey: typeKey,
            _typeLabel: typeLabel,
            _channelKey: channelKey,
            _channelName: prettyNames[channelKey] || channelKey,
            date_created: model.date_created,
            date_edited: model.date_edited
          }
          const codeParts = []
          const levelNames = []
          let hasAnyValue = false
          this.taxonomySelectLevels.forEach(level => {
            const value = saved[level]
            const name = (value && typeof value === 'object') ? String(value.name || '') : ''
            const abbreviation = (value && typeof value === 'object') ? String(value.abbreviation || '') : ''
            row['_' + level.toLowerCase()] = name
            row['_' + level.toLowerCase() + 'Abbr'] = abbreviation
            if (name) {
              hasAnyValue = true
              levelNames.push(name)
            }
            // The code mirrors the Taxonomy column of functions/touchpoints.csv:
            // the literal 'null' stands in for a level with no value.
            codeParts.push(abbreviation || 'null')
          })
          const freeText = saved.L6 ? String(saved.L6) : ''
          row._l6 = freeText
          if (freeText) {
            hasAnyValue = true
            levelNames.push(freeText)
          }
          codeParts.push(freeText || 'null')
          // A channel with no taxonomy at all gets a blank code rather than a
          // meaningless 'null_null_null_null_null_null_null'.
          row._code = hasAnyValue ? codeParts.join('_') : ''
          // Every searchable field, concatenated and lowercased once here so
          // customFilterTaxonomy is a single includes() per call instead of ~13
          // String() + toLowerCase() allocations. Vuetify runs the custom filter
          // once per header per row, so that cost was paid over and over on each
          // (debounced) keystroke. NUL separates the fields: it can never be typed
          // into the search box, so no match can straddle two fields.
          row._search = [row._modelId, row._modelName, row._typeLabel, row._channelKey, row._channelName, row._code, modifiedText]
            .concat(levelNames)
            .join('\u0000')
            .toLowerCase()
          rows.push(row)
        })
      })
      this.processedTaxonomyRows = rows
    },
    customFilterTaxonomy (value, search, item) {
      if (!search) return true
      // `value` is ignored on purpose: the row carries a pre-lowercased blob of
      // every searchable field (built in updateProcessedTaxonomyRows), so the
      // match is column-independent and costs one includes() with no allocation.
      return item._search.includes(search.toLowerCase())
    },
    isTaxonomyTypeSelected (key) {
      return !this.taxonomyExcludedTypes.includes(key)
    },
    toggleTaxonomyType (key) {
      const idx = this.taxonomyExcludedTypes.indexOf(key)
      if (idx === -1) {
        this.taxonomyExcludedTypes.push(key)
      } else {
        this.taxonomyExcludedTypes.splice(idx, 1)
      }
    },
    toggleTaxonomyAllTypes () {
      if (this.taxonomyExcludedTypes.length > 0) {
        this.taxonomyExcludedTypes = []
      } else {
        this.taxonomyExcludedTypes = this.taxonomyTypeOptions.map(opt => opt.key)
      }
    },
    downloadTaxonomyCsv () {
      // Exports exactly what the table shows: the funnels (displayedTaxonomyRows)
      // plus the debounced search, which v-data-table applies internally. The search
      // pass runs here rather than in a computed so it costs one full scan per export
      // click instead of one per keystroke on top of the table's own filtering.
      const rows = this.debouncedSearch
        ? this.displayedTaxonomyRows.filter(row => this.customFilterTaxonomy(null, this.debouncedSearch, row))
        : this.displayedTaxonomyRows
      const escapeCsv = value => '"' + String(value === null || value === undefined ? '' : value).replace(/"/g, '""') + '"'
      const levels = this.taxonomySelectLevels
      // Column set of functions/touchpoints.csv (values, then abbreviations, then
      // the joined code), prefixed with the model context each row belongs to.
      const header = ['model_id', 'model', 'type', 'channel_key', 'channel']
        .concat(levels).concat(['L6'])
        .concat(levels.map(level => level + '_')).concat(['L6_'])
        .concat(['Taxonomy'])
      const lines = [header.map(escapeCsv).join(',')]
      rows.forEach(row => {
        const cells = [row._modelId, row._modelName, row._typeLabel, row._channelKey, row._channelName]
        levels.forEach(level => cells.push(row['_' + level.toLowerCase()]))
        cells.push(row._l6)
        levels.forEach(level => cells.push(row['_' + level.toLowerCase() + 'Abbr']))
        // Free text has no abbreviation, so L6_ repeats L6 (as the source CSV does).
        cells.push(row._l6)
        cells.push(row._code)
        lines.push(cells.map(escapeCsv).join(','))
      })
      // Leading BOM so Excel reads the accents correctly (the source CSV has one).
      const csvContent = '\ufeff' + lines.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'channel-taxonomy.csv'
      link.click()
      URL.revokeObjectURL(url)
    },
    customFilterMetadata (value, search, item) {
      if (!search) return true

      const searchLower = search.toLowerCase()
      const searchableFields = [
        item.id,
        item.name,
        item._typeLabel,
        this.creatorLabel(item),
        this.dateModified(item),
        item._metaClient,
        item._metaBrand,
        item._metaCategory,
        item._metaSubCategory,
        item._metaCountry
      ]

      return searchableFields.some(field => {
        if (field === null || field === undefined) return false
        return String(field).toLowerCase().includes(searchLower)
      })
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'customModels',
      'mmmModels',
      'referenceModels',
      'ekrmModelsObj'
    ]),
    ...mapGetters('clients', [
      'clients',
      'groupClients',
      'currentClientObject'
    ]),
    ...mapGetters('settings', [
      'countries'
    ]),
    customHeaders () {
      return [
        { text: this.$t('views.manageCustomModels.headerName'), value: 'name', cellClass: 'wrap-long-text' },
        { text: this.$t('views.manageCustomModels.headerCategory'), value: 'reference_model.config.category', cellClass: 'wrap-long-text' },
        { text: this.$t('views.manageCustomModels.headerReference'), value: 'custom_model.brand', cellClass: 'wrap-long-text' },
        { text: this.$t('views.manageCustomModels.headerMonthlySpend'), value: 'custom_model.average_monthly_invest' },
        { text: this.$t('views.manageCustomModels.headerMonthlySales'), value: 'custom_model.average_monthly_sales' },
        { text: this.$t('views.manageCustomModels.headerCountry'), value: '_countryName' },
        { text: this.$t('views.manageCustomModels.headerQuality'), value: 'model_report.quality' },
        { text: this.$t('views.manageCustomModels.headerModified'), value: 'date_created' },
        { text: this.$t('views.manageCustomModels.headerCreator'), value: 'created_by', width: '15ch' },
        { text: this.$t('views.manageCustomModels.headerActions'), value: 'action', sortable: false }
      ]
    },
    mmmHeaders () {
      // Mirrors the App.vue Model Selection modal's AD-HOC columns, plus this view's
      // Actions column. The metadata columns (Client/Category/Subcategory) only appear
      // when at least one model in the pool has a value for them.
      const headers = [{ text: this.$t('views.manageCustomModels.headerName'), value: 'name', cellClass: 'wrap-long-text' }]
      const pushMeta = key => {
        if (this.availableAdHocMetadata(key).length > 0) {
          const cfg = this.adHocMetadataFilterConfigs.find(c => c.key === key)
          headers.push({ text: this.$t('views.manageCustomModels.' + cfg.headerKey), value: cfg.valueField, cellClass: 'wrap-long-text' })
        }
      }
      pushMeta('client')
      headers.push({ text: this.$t('views.manageCustomModels.metaHeaderBrand'), value: 'brandNames', cellClass: 'wrap-long-text' })
      pushMeta('category')
      pushMeta('sub-category')
      headers.push({ text: this.$t('views.manageCustomModels.headerCountry'), value: '_countryName' })
      headers.push({ text: this.$t('views.manageCustomModels.headerModified'), value: 'date_created' })
      headers.push({ text: this.$t('views.manageCustomModels.headerActions'), value: 'action', sortable: false })
      return headers
    },
    availableAdHocCountries () {
      // Unique countries across the ad-hoc pool. The same country expressed two ways
      // (registry code vs. client_metadata name) collapses via resolveCountryKey, and
      // the client_metadata name is preferred for the option label.
      const metadataLabel = new Map()
      const fallbackLabel = new Map()
      this.mmmModels.forEach(item => {
        const rawValue = this.getAdHocCountryCode(item)
        const key = this.resolveCountryKey(rawValue)
        if (key === null) {
          return
        }
        const label = this.getCountryName(rawValue)
        if (item.client_metadata && item.client_metadata.country) {
          if (!metadataLabel.has(key)) {
            metadataLabel.set(key, label)
          }
        } else if (!fallbackLabel.has(key)) {
          fallbackLabel.set(key, label)
        }
      })
      const keys = new Set([...metadataLabel.keys(), ...fallbackLabel.keys()])
      return Array.from(keys)
        .map(key => ({ value: key, text: metadataLabel.get(key) || fallbackLabel.get(key) }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    filteredAdHocCountries () {
      if (!this.adHocCountrySearch) {
        return this.availableAdHocCountries
      }
      return this.availableAdHocCountries.filter(c =>
        c.text.toLowerCase().includes(this.adHocCountrySearch.toLowerCase())
      )
    },
    hasActiveAdHocCountryFilter () {
      const total = this.availableAdHocCountries.length
      const selectedCount = this.selectedAdHocCountries.length
      return selectedCount > 0 && selectedCount < total
    },
    selectAllAdHocCountryIcon () {
      const visibleValues = this.availableAdHocCountries
        .filter(c => !this.adHocCountrySearch || c.text.toLowerCase().includes(this.adHocCountrySearch.toLowerCase()))
        .map(c => c.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.selectedAdHocCountries.includes(v))
      const someVisibleSelected = visibleValues.some(v => this.selectedAdHocCountries.includes(v))
      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllAdHocCountryText () {
      const visible = this.availableAdHocCountries
        .filter(c => !this.adHocCountrySearch || c.text.toLowerCase().includes(this.adHocCountrySearch.toLowerCase()))
      if (this.adHocCountrySearch && visible.length > 0) {
        return this.$t('views.manageCustomModels.selectAllVisible', { count: visible.length })
      }
      return this.$t('views.manageCustomModels.filterSelectAll')
    },
    availableAdHocBrands () {
      // Unique resolved brands (client_metadata.brand or config.brands keys) across the pool.
      const brands = new Set()
      this.mmmModels.forEach(item => {
        this.getModelBrands(item).forEach(brand => brands.add(brand))
      })
      return Array.from(brands).sort((a, b) => a.localeCompare(b)).map(brand => ({ value: brand, text: brand }))
    },
    filteredAdHocBrands () {
      if (!this.adHocBrandSearch) {
        return this.availableAdHocBrands
      }
      return this.availableAdHocBrands.filter(b =>
        b.text.toLowerCase().includes(this.adHocBrandSearch.toLowerCase())
      )
    },
    hasActiveAdHocBrandFilter () {
      const total = this.availableAdHocBrands.length
      const selectedCount = this.selectedAdHocBrands.length
      return selectedCount > 0 && selectedCount < total
    },
    selectAllAdHocBrandIcon () {
      const visibleValues = this.availableAdHocBrands
        .filter(b => !this.adHocBrandSearch || b.text.toLowerCase().includes(this.adHocBrandSearch.toLowerCase()))
        .map(b => b.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.selectedAdHocBrands.includes(v))
      const someVisibleSelected = visibleValues.some(v => this.selectedAdHocBrands.includes(v))
      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllAdHocBrandText () {
      const visible = this.availableAdHocBrands
        .filter(b => !this.adHocBrandSearch || b.text.toLowerCase().includes(this.adHocBrandSearch.toLowerCase()))
      if (this.adHocBrandSearch && visible.length > 0) {
        return this.$t('views.manageCustomModels.selectAllVisible', { count: visible.length })
      }
      return this.$t('views.manageCustomModels.filterSelectAll')
    },
    visibleAdHocMetadataConfigs () {
      // Only the metadata filters that have at least one value across the pool are surfaced.
      return this.adHocMetadataFilterConfigs.filter(cfg => this.availableAdHocMetadata(cfg.key).length > 0)
    },
    filteredMmmModels () {
      // Applies the country/brand/metadata funnels to the processed ad-hoc rows. Each
      // filter only narrows when a strict subset is selected (all-selected = show all),
      // matching the App.vue modal's modelDialogItems behaviour. One row per version family,
      // its current version; older versions follow it, as rows of their own, when it is expanded.
      let items = this.processedMmmModels.filter(item => !item.isOlderVersion)

      if (this.selectedAdHocCountries.length > 0 && this.selectedAdHocCountries.length < this.availableAdHocCountries.length) {
        items = items.filter(item => {
          const key = this.resolveCountryKey(this.getAdHocCountryCode(item))
          return key !== null && this.selectedAdHocCountries.includes(key)
        })
      }

      if (this.selectedAdHocBrands.length > 0 && this.selectedAdHocBrands.length < this.availableAdHocBrands.length) {
        items = items.filter(item =>
          this.getModelBrands(item).some(brand => this.selectedAdHocBrands.includes(brand))
        )
      }

      this.adHocMetadataFilterConfigs.forEach(cfg => {
        const selected = this.adHocMetadataSelected[cfg.key]
        const available = this.availableAdHocMetadata(cfg.key)
        if (selected.length > 0 && selected.length < available.length) {
          items = items.filter(item => {
            const val = this.getMetadataValue(item, cfg.key)
            return val !== null && selected.includes(val)
          })
        }
      })

      const rows = []
      items.forEach(item => {
        rows.push(item)
        if (this.isAdHocFamilyExpanded(item)) {
          rows.push(...this.adHocVersionFamilies.get(versionFamilyId(item)).slice(0, -1).reverse())
        }
      })
      return rows
    },
    adHocVersionFamilies () {
      return groupByFamily(this.processedMmmModels)
    },
    // filteredMmmModels searched and sorted, older versions right below their current version
    adHocTableItems () {
      const search = this.debouncedSearch ? this.debouncedSearch.trim() : ''
      const items = search ? filterFamilyRows(this.filteredMmmModels, item => this.matchesAdHocSearch(item, search)) : this.filteredMmmModels
      return sortFamilyRows(items, current => this.customSort(current, this.adHocSortBy, this.adHocSortDesc))
    },
    adHocFamilyCount () {
      return countFamilyRows(this.adHocTableItems)
    },
    adHocPageItems () {
      return familyRowsPage(this.adHocTableItems, this.adHocPage, this.adHocItemsPerPage)
    },
    adHocLastPage () {
      return lastFamilyPage(this.adHocFamilyCount, this.adHocItemsPerPage)
    },
    automaticHeaders () {
      return [
        { text: this.$t('views.manageCustomModels.headerName'), value: 'name', cellClass: 'wrap-long-text' },
        { text: this.$t('views.manageCustomModels.headerCountry'), value: '_countryName' },
        { text: this.$t('views.manageCustomModels.headerQuality'), value: 'model_report.quality' },
        { text: this.$t('views.manageCustomModels.headerModified'), value: 'date_created' },
        { text: this.$t('views.manageCustomModels.headerCreator'), value: 'created_by' },
        { text: this.$t('views.manageCustomModels.headerActions'), value: 'action', sortable: false }
      ]
    },
    metadataFieldDefs () {
      // One definition per client_metadata category (mirrors ModelSettings' metadataFields).
      // `key` is the Firestore key; `prop` is the flat, dot/dash-free lean-row property used
      // as the Vuetify header value, dynamic slot name, _.get sort path and filter-state key.
      return [
        { key: 'client', prop: '_metaClient', label: this.$t('views.manageCustomModels.metaHeaderClient') },
        { key: 'brand', prop: '_metaBrand', label: this.$t('views.manageCustomModels.metaHeaderBrand') },
        { key: 'category', prop: '_metaCategory', label: this.$t('views.manageCustomModels.metaHeaderCategory') },
        { key: 'sub-category', prop: '_metaSubCategory', label: this.$t('views.manageCustomModels.metaHeaderSubCategory') },
        { key: 'country', prop: '_metaCountry', label: this.$t('views.manageCustomModels.metaHeaderCountry') }
      ]
    },
    metadataColumns () {
      // Single O(n·5) pass: collect distinct non-blank values + a blank flag per field.
      // Only fields that actually have data become columns ("only categories with data").
      const defs = this.metadataFieldDefs
      const sets = {}
      const hasBlank = {}
      defs.forEach(d => { sets[d.prop] = new Set(); hasBlank[d.prop] = false })
      for (const m of this.processedMetadataModels) {
        for (const d of defs) {
          const v = m[d.prop]
          if (v === '' || v === null || v === undefined) {
            hasBlank[d.prop] = true
          } else {
            sets[d.prop].add(v)
          }
        }
      }
      const collator = new Intl.Collator(undefined, { sensitivity: 'base', ignorePunctuation: true })
      const cols = []
      for (const d of defs) {
        if (sets[d.prop].size === 0) {
          continue
        }
        const options = Array.from(sets[d.prop]).sort((a, b) => collator.compare(a, b)).map(v => ({ value: v, label: v }))
        if (hasBlank[d.prop]) {
          options.push({ value: this.metadataBlankSentinel, label: this.$t('views.manageCustomModels.filterNone') })
        }
        cols.push({ key: d.key, prop: d.prop, label: d.label, options: options })
      }
      return cols
    },
    metadataHeaders () {
      const metaCols = this.metadataColumns.map(c => ({ text: c.label, value: c.prop }))
      return [
        { text: this.$t('views.manageCustomModels.headerName'), value: 'name' },
        { text: this.$t('views.manageCustomModels.headerType'), value: '_typeLabel' },
        ...metaCols,
        { text: this.$t('views.manageCustomModels.headerModified'), value: 'date_created' },
        { text: this.$t('views.manageCustomModels.headerCreator'), value: 'created_by' },
        { text: this.$t('views.manageCustomModels.headerActions'), value: 'action', sortable: false }
      ]
    },
    metadataModelsLoading () {
      // Loading while the eager custom models haven't arrived, or the tab
      // triggered a lazy mmm load that hasn't completed yet.
      return this.customModelsLoading || this.mmmModelsLoading
    },
    metadataTypeOptions () {
      return this.typeOptionsFrom(this.processedMetadataModels)
    },
    metadataAllTypesSelected () {
      return this.metadataExcludedTypes.length === 0
    },
    displayedMetadataModels () {
      // Cheap O(n) pass applying the Type funnel + every metadata-column funnel.
      // Recomputes only when the lean array or any selection changes. Search and
      // sort compose on top of this.
      const typeExcluded = this.metadataExcludedTypes.length
        ? new Set(this.metadataExcludedTypes)
        : null
      // Build a {prop -> Set} only for fields that actually exclude something.
      const valueExcluded = []
      for (const d of this.metadataFieldDefs) {
        const arr = this.metadataExcludedValues[d.prop]
        if (arr && arr.length) {
          valueExcluded.push({ prop: d.prop, set: new Set(arr) })
        }
      }
      if (!typeExcluded && valueExcluded.length === 0) {
        return this.processedMetadataModels
      }
      const blank = this.metadataBlankSentinel
      return this.processedMetadataModels.filter(m => {
        if (typeExcluded && typeExcluded.has(m._typeKey)) {
          return false
        }
        for (const f of valueExcluded) {
          const raw = m[f.prop]
          const key = (raw === '' || raw === null || raw === undefined) ? blank : raw
          if (f.set.has(key)) {
            return false
          }
        }
        return true
      })
    },
    taxonomySelectLevels () {
      // Levels stored as { name, abbreviation } objects; L6 is free text and
      // handled apart. Mirrors ChannelTaxonomySettings' own list.
      return ['L0', 'L1', 'L2', 'L3', 'L4', 'L5']
    },
    taxonomyLevelDefs () {
      // One definition per taxonomy level, L0 through L6. `prop` is the flat lean-row
      // property used as the Vuetify header value, dynamic slot name, sort path and
      // filter-state key. Labels reuse the editor's keys so the level names stay in
      // sync with ChannelTaxonomySettings, rendered as "L0 - Weight".
      return ['L0', 'L1', 'L2', 'L3', 'L4', 'L5', 'L6'].map(level => ({
        level: level,
        prop: '_' + level.toLowerCase(),
        label: level + ' - ' + this.$t('components.ModelSettings.taxonomyLevel' + level)
      }))
    },
    taxonomyColumns () {
      // Funnel-able columns: the channel plus every taxonomy level. Single
      // O(n·8) pass collecting distinct values + a blank flag per field.
      // Unlike metadataColumns, columns are NOT dropped when empty: the levels
      // are a fixed, known schema, so an untagged pool must still show them.
      const defs = [{ prop: '_channelName', label: this.$t('views.manageCustomModels.taxonomyHeaderChannel') }]
        .concat(this.taxonomyLevelDefs.map(d => ({ prop: d.prop, label: d.label })))
      const sets = {}
      const hasBlank = {}
      defs.forEach(d => { sets[d.prop] = new Set(); hasBlank[d.prop] = false })
      for (const row of this.processedTaxonomyRows) {
        for (const d of defs) {
          const v = row[d.prop]
          if (v === '' || v === null || v === undefined) {
            hasBlank[d.prop] = true
          } else {
            sets[d.prop].add(v)
          }
        }
      }
      const collator = new Intl.Collator(undefined, { sensitivity: 'base', ignorePunctuation: true })
      return defs.map(d => {
        const options = Array.from(sets[d.prop]).sort((a, b) => collator.compare(a, b)).map(v => ({ value: v, label: v }))
        if (hasBlank[d.prop]) {
          options.push({ value: this.metadataBlankSentinel, label: this.$t('views.manageCustomModels.filterNone') })
        }
        return { prop: d.prop, label: d.label, options: options }
      })
    },
    taxonomyHeaders () {
      // Modified goes last before Actions, after Taxonomy, mirroring the Metadata tab's
      // layout — this table scrolls horizontally, so it may need scrolling to be seen
      // even though it is the default sort column. Creator is still left out: it would
      // repeat on every channel row of a model and adds nothing the Metadata tab does
      // not already show.
      // Only the first column stays filterable: Vuetify calls the custom filter once
      // per filterable header per row (VDataTable's searchTableItems), and
      // customFilterTaxonomy already matches against the whole row, so the other ten
      // calls would repeat the same answer. Excluding them costs no reachable row and
      // divides the per-keystroke work by eleven. At least one must stay filterable —
      // with none, Vuetify's .some() is false for every row and the table empties.
      return [
        { text: this.$t('views.manageCustomModels.taxonomyHeaderModel'), value: '_modelName' },
        { text: this.$t('views.manageCustomModels.headerType'), value: '_typeLabel', filterable: false },
        ...this.taxonomyColumns.map(c => ({ text: c.label, value: c.prop, filterable: false })),
        { text: this.$t('views.manageCustomModels.taxonomyHeaderCode'), value: '_code', filterable: false },
        { text: this.$t('views.manageCustomModels.headerModified'), value: 'date_created', filterable: false },
        { text: this.$t('views.manageCustomModels.headerActions'), value: 'action', sortable: false, filterable: false }
      ]
    },
    taxonomyTypeOptions () {
      return this.typeOptionsFrom(this.processedTaxonomyRows)
    },
    taxonomyAllTypesSelected () {
      return this.taxonomyExcludedTypes.length === 0
    },
    taxonomyRowsLoading () {
      // Loading while the eager custom models haven't arrived, or the tab
      // triggered a lazy mmm load that hasn't completed yet.
      return this.customModelsLoading || this.mmmModelsLoading
    },
    displayedTaxonomyRows () {
      // Cheap O(n) pass applying the Type funnel + every column funnel, mirroring
      // displayedMetadataModels. Search and sort compose on top of this.
      const typeExcluded = this.taxonomyExcludedTypes.length
        ? new Set(this.taxonomyExcludedTypes)
        : null
      const valueExcluded = []
      for (const col of this.taxonomyColumns) {
        const arr = this.taxonomyExcludedValues[col.prop]
        if (arr && arr.length) {
          valueExcluded.push({ prop: col.prop, set: new Set(arr) })
        }
      }
      if (!typeExcluded && valueExcluded.length === 0) {
        return this.processedTaxonomyRows
      }
      const blank = this.metadataBlankSentinel
      return this.processedTaxonomyRows.filter(row => {
        if (typeExcluded && typeExcluded.has(row._typeKey)) {
          return false
        }
        for (const f of valueExcluded) {
          const raw = row[f.prop]
          const key = (raw === '' || raw === null || raw === undefined) ? blank : raw
          if (f.set.has(key)) {
            return false
          }
        }
        return true
      })
    },
    allReferences: function () {
      return Object.keys(this.referenceModels).concat(Object.keys(this.ekrmModelsObj))
    }
  },
  watch: {
    currentTab: function (newVal) {
      // MEMORY OPTIMIZATION: Lazy load MMM models only when the Ad-Hoc, Metadata
      // or Channel Taxonomy tab needs them
      if ((newVal === 1 || newVal === 3 || newVal === 4) && !this.mmmModelsLoaded) {
        this.mmmModelsLoading = true
        this.loadMmmModels()
        this.mmmModelsLoaded = true
      }
      // MEMORY OPTIMIZATION: Activate + build the Metadata array only on first open
      if (newVal === 3) {
        this.metadataModelsActive = true
        this.updateProcessedMetadataModels()
      }
      // Same lazy activation for the (much larger) per-channel taxonomy rows
      if (newVal === 4) {
        this.taxonomyRowsActive = true
        this.updateProcessedTaxonomyRows()
      }
    },
    'authUser.clients': function () {
      if (!this.authUser.isManager && !this.authUser.isAdmin && this.authUser.clients && this.authUser.clients.length > 0) {
        this.loadClients({
          clients: this.authUser.clients
        })
      }
    },
    'authUser.groups': function () {
      if (!this.authUser.isManager && !this.authUser.isAdmin && this.authUser.groups && this.authUser.groups.length > 0) {
        this.loadGroupClients({
          groups: this.authUser.groups,
          user: this.authUser.uid
        })
      }
    },
    search: function (newVal) {
      this.debouncedSearchUpdate(newVal)
    },
    debouncedSearch: function () {
      // Re-process models when search term changes (debounced)
      this.updateProcessedCustomModels()
      this.updateProcessedAutomaticModels()
      this.updateProcessedMetadataModels()
    },
    customModels: {
      handler: function () {
        this.updateProcessedCustomModels()
        this.updateProcessedAutomaticModels()
        this.updateProcessedMetadataModels()
        this.updateProcessedTaxonomyRows()
        // Set loading to false when custom models data arrives
        if (this.customModelsLoading) {
          this.customModelsLoading = false
        }
        if (this.automaticModelsLoading) {
          this.automaticModelsLoading = false
        }
      }
      // Removed deep:true to avoid memory leaks from watching every property change
      // Array changes (add/remove/sort) are still detected automatically
    },
    clients: {
      handler: function () {
        this.updateProcessedCustomModels()
        this.updateProcessedAutomaticModels()
      }
      // Removed deep:true - only watching for array reference changes
    },
    groupClients: {
      handler: function () {
        this.updateProcessedCustomModels()
        this.updateProcessedAutomaticModels()
      }
      // Removed deep:true - only watching for array reference changes
    },
    'authUser.uid': function () {
      this.updateProcessedCustomModels()
      this.updateProcessedAutomaticModels()
    },
    // Vuetify only keeps the page in range when it pages the rows itself
    adHocLastPage (lastPage) {
      if (this.adHocPage > lastPage) {
        this.adHocPage = lastPage
      }
    },
    mmmModels: {
      handler: function () {
        // Update processed array with memory optimizations
        this.updateProcessedMmmModels()
        this.updateProcessedMetadataModels()
        this.updateProcessedTaxonomyRows()
        // Default the Ad-Hoc brand/country funnels to "all selected" once the data arrives.
        this.initAdHocFilterSelections()
        // Set loading to false when MMM models data arrives
        if (this.mmmModelsLoading) {
          this.mmmModelsLoading = false
        }
      }
      // Removed deep:true to avoid memory leaks from watching every property change
      // Array changes (add/remove/sort) are still detected automatically
    }
  },
  components: {
    CreateCustomModel,
    DeletedModels,
    EditModelData,
    PlanImporter,
    ModelSettings
  },
  created: function () {
    this.loadReferenceModels()
    this.loadCustomModels()
    // MEMORY OPTIMIZATION: loadMmmModels() is now called lazily when Ad-Hoc Models tab is opened
    this.loadEkrmModels()

    // Create debounced search update function
    this.debouncedSearchUpdate = _.debounce((value) => {
      this.debouncedSearch = value
    }, 300)

    // Initialize processed custom models
    this.updateProcessedCustomModels()
    this.updateProcessedAutomaticModels()
  },
  beforeDestroy: function () {
    // Clean up Vuex store Firestore listeners to prevent memory leaks
    this.$store.dispatch('manager/cleanupListeners')
    // Cancel any pending debounced operations
    if (this.debouncedSearchUpdate && this.debouncedSearchUpdate.cancel) {
      this.debouncedSearchUpdate.cancel()
    }
    // Clear processed models to free memory
    this.processedCustomModels = []
    this.processedMmmModels = []
    this.processedAutomaticModels = []
    this.processedMetadataModels = []
    this.metadataExcludedTypes = []
    this.metadataExcludedValues = {
      _metaClient: [],
      _metaBrand: [],
      _metaCategory: [],
      _metaSubCategory: [],
      _metaCountry: []
    }
    this.metadataValueMenus = {
      _metaClient: false,
      _metaBrand: false,
      _metaCategory: false,
      _metaSubCategory: false,
      _metaCountry: false
    }
    this.processedTaxonomyRows = []
    this.taxonomyExcludedTypes = []
    this.taxonomyExcludedValues = {
      _channelName: [], _l0: [], _l1: [], _l2: [], _l3: [], _l4: [], _l5: [], _l6: []
    }
    this.taxonomyValueMenus = {
      _channelName: false, _l0: false, _l1: false, _l2: false, _l3: false, _l4: false, _l5: false, _l6: false
    }
    this.selectedAdHocCountries = []
    this.selectedAdHocBrands = []
    this.adHocMetadataSelected = { client: [], category: [], 'sub-category': [] }
    this.adHocMetadataSearch = { client: '', category: '', 'sub-category': '' }
    this.adHocMetadataMenu = { client: false, category: false, 'sub-category': false }
  }
}
